import React, { useEffect } from 'react';
import './email_template_field.scss';
import { Button } from '../../Button/Button';
import { t } from '../../../locales/translate';
import { Dialog } from '../../dialog/dialog';
import { Stack } from '../../Stack/Stack';
import { useLazyQueryHandler } from '../../../model/graphql';
import { graphql } from '../../../generated';

interface Props {
  eventId: string;
  data: {
    firstName: string | null;
    lastName: string | null;
    email: string | null;
    phone: string | null;
    ticketsCount: number | null;
    text: string | null;
  };
}

export const EVENT_LOCAL_TICKET_TEMPLATE = graphql(`
  query EventLocalTicketTemplate(
    $eventId: ID!
    $firstName: String
    $lastName: String
    $email: String
    $phone: String
    $ticketsCount: Int
    $text: String
  ) {
    event(id: $eventId) {
      id
      localTicketEmailTemplate {
        id
        enabled
        replyToEmail
        templateHtml
        templatePreviewHtml(
          firstName: $firstName
          lastName: $lastName
          email: $email
          phone: $phone
          ticketsCount: $ticketsCount
          text: $text
        )
      }
    }
  }
`);

export function EmailTemplateField({ eventId, data: propData }: Props): JSX.Element {
  const [getTemplate, { loading, data }] = useLazyQueryHandler(EVENT_LOCAL_TICKET_TEMPLATE);

  const [displayPreview, setDisplayPreview] = React.useState(false);

  const template = data?.event?.localTicketEmailTemplate;

  const title = template?.enabled ? t.model.ticketEmailTemplate.tags.custom : t.model.ticketEmailTemplate.tags.default;

  const handlePreview = (): void => {
    getTemplate({
      variables: {
        eventId,
        ...propData,
      },
    });
    setDisplayPreview(true);
  };

  // First load to display the correct title
  useEffect(() => {
    getTemplate({
      variables: {
        eventId,
        ...propData,
      },
    });
  }, []);

  return (
    <>
      <div className="email-template-field">
        <header className="email-template-field__header">
          <div className="email-template-field__title">{t.model.ticketEmailTemplate.emailTemplate}</div>
          <div className="email-template-field__value">{title}</div>
        </header>
        <footer>
          <Button
            title={t.model.ticketEmailTemplate.previewEmail}
            type="secondary"
            condensed
            onClick={() => handlePreview()}
            loading={loading}
          />
        </footer>
      </div>
      <Dialog
        open={displayPreview && !loading}
        header={
          <Stack size="16">
            <h2 className="email-template-field__preview-title">
              {t.model.ticketEmailTemplate.contentAndStylePreview}
            </h2>
            <div className="email-template-field__preview-value">{title}</div>
          </Stack>
        }
        footer={<Button title={t.actions.cancel('')} onClick={() => setDisplayPreview(false)} />}
      >
        {template?.templatePreviewHtml && (
          <iframe
            srcDoc={template?.templatePreviewHtml}
            style={{
              width: '100%',
              height: 'min(80vh, 600px)',
            }}
          />
        )}
      </Dialog>
    </>
  );
}
